import {
    Box,
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    Paper,
    TableBody,
    Alert,
    CircularProgress,
    Button,
    Select,
    MenuItem,
    FormControl,
    TextField,
    Typography
} from '@mui/material';
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import ClearIcon from '@mui/icons-material/Clear';
import { getIgnitionFuel, postIgnitionFuel } from '../../../../../actions/rof-burns/rof-actions';

export default function RofFuel({ fuel, selectedProject }) {

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [fuelState, setFuelState] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        if (fuel?.data) {
            setFuelState(
                fuel.data.map((d) => ({
                    ...d,
                    disabled: true
                }))
            );
        }
    }, [fuel]);

    const handleAddEntry = () => {
        const newEntry = {
            rowId: uuidv4(),
            date: '',
            fuel_type: '',
            refill_volume: '',
            source_document: null,
            disabled: false
        };
        setFuelState(prev => [...prev, newEntry]);

    }

    useEffect(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, [fuelState])

    // Handle date selection
    const handleDateSelection = (date, rowId) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        handleInputChange(rowId, 'date', formattedDate);
    };

    const handleSourceDoc = (e, rowId) => {
        const file = e.target.files[0];
        handleInputChange(rowId, 'source_document', file);
    };

    // Helper: check if row is empty (all fields blank)
    const isEmptyRow = (row) => {
        return (
            !row.date &&
            !row.fuel_type &&
            !row.refill_volume
        );
    };

    // Helper: check if row is complete (all required fields filled)
    const isCompleteRow = (row) => {
        return (
            !!row.date &&
            !!row.fuel_type &&
            !!row.refill_volume
        );
    };

    // Helper: validate a complete row, return error message or null
    const validateCompleteRow = (row) => {
        if (!row.date) return "Date is required.";
        if (!row.fuel_type) return "Fuel type is required.";
        if (!row.refill_volume) return "Refill volume is required.";
        // Add more validation as needed
        return null;
    };

    const handleSubmitData = async () => {
        setLoading(true);
        setError(false);
        setErrorMsg && setErrorMsg("");

        try {
            const emptyRows = [];
            const partialRows = [];
            const completeRows = [];

            // Only consider rows with rowId (i.e., new/unsaved)
            const rowsToSubmit = fuelState.filter(row => row.rowId && !row.id);

            rowsToSubmit.forEach((row, idx) => {
                if (isEmptyRow(row)) {
                    emptyRows.push(row);
                } else if (isCompleteRow(row)) {
                    completeRows.push({ index: idx, row });
                } else {
                    partialRows.push({ index: idx, row });
                }
            });

            if (partialRows.length > 0) {
                const first = partialRows[0];
                setError(true);
                setErrorMsg &&
                    setErrorMsg(
                        `Row ${first.index + 1} is partially completed. Please fill all required fields or clear the row.`
                    );
                setLoading(false);
                return;
            }

            for (const { index, row } of completeRows) {
                const msg = validateCompleteRow(row);
                if (msg) {
                    setError(true);
                    setErrorMsg && setErrorMsg(`Row ${index + 1}: ${msg}`);
                    setLoading(false);
                    return;
                }
            }

            if (completeRows.length === 0) {
                setLoading(false);
                return;
            }

            const payload = [];
            completeRows.forEach(({ row }, idx) => {
                const entry = {
                    date: row.date,
                    fuel_type: row.fuel_type,
                    refill_volume: String(row.refill_volume),
                    source_document: null,
                };
                if (row.source_document && typeof row.source_document !== "string") {
                    entry.source_document = `source_document_${idx}`;
                }
                payload.push(entry);
            });

            const formData = new FormData();
            formData.append("data", JSON.stringify(payload));
            completeRows.forEach(({ row }, idx) => {
                if (row.source_document && typeof row.source_document !== "string") {
                    formData.append(`source_document_${idx}`, row.source_document);
                }
            });

            dispatch(postIgnitionFuel(formData, selectedProject.id)).then(() => {
                dispatch(getIgnitionFuel(selectedProject.id));
            });
        } catch (e) {
            setError(true);
            setErrorMsg && setErrorMsg("Failed to submit data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (rowId, field, value) => {
        setFuelState(prev =>
            prev.map(row =>
                row.rowId === rowId ? { ...row, [field]: value } : row
            )
        );
    };

    const fuelTypes = [{ id: 0, name: 'diesel' }, { id: 1, name: 'petrol' }]

    const handleClearEntry = (rowIdToRemove) => {
        setFuelState((prevState) =>
            prevState.filter((item) => item.rowId !== rowIdToRemove)
        );
    }

    return (
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='h5'>Auxillary and Ignition Fuel</Typography>
            <TableContainer component={Paper} sx={{ height: 'auto' }}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">
                                Date
                            </TableCell>
                            <TableCell align="left">
                                Fuel Type (ff)
                            </TableCell>
                            <TableCell align="left">
                                Refill Volume (gal)
                            </TableCell>
                            <TableCell align="left">
                                Source document
                            </TableCell>
                            <TableCell align="left">
                                Remove Row
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fuelState?.map((data, i) => {
                            const isString = typeof data.source_document === 'string' && data.source_document !== 'null';
                            return (
                                <TableRow key={i}>
                                    <TableCell align="left">
                                        <FormControl>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    name="date"
                                                    value={data.date ? moment(data.date, 'YYYY-MM-DD') : null}
                                                    onChange={date => handleDateSelection(date, data.rowId)}
                                                    disabled={data.disabled ?? false}
                                                    format="YYYY-MM-DD"
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Select
                                            name="fuel_type"
                                            value={data.fuel_type}
                                            disabled={data.disabled ?? false}
                                            fullWidth
                                            onChange={e => handleInputChange(data.rowId, 'fuel_type', e.target.value)}
                                        >
                                            {fuelTypes.map(type => (
                                                <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TextField
                                            name="refill_volume"
                                            value={data.refill_volume}
                                            onChange={e => handleInputChange(data.rowId, 'refill_volume', e.target.value)}
                                            type="number"
                                            disabled={data.disabled ?? false}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        {isString ? (
                                            <a href={data.source_document} target="_blank" rel="noopener noreferrer">
                                                {data.source_document}
                                            </a>
                                        ) : (
                                            <TextField
                                                disabled={data.disabled ?? false}
                                                name="source_document"
                                                type="file"
                                                inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.png' }}
                                                onChange={e => handleSourceDoc(e, data.rowId)}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="left">
                                        {data.rowId && i === fuelState.length - 1 ? (
                                            <Box onClick={() => handleClearEntry(data.rowId)}
                                                sx={{ cursor: 'pointer', color: 'red', mt: 1 }}>
                                                <ClearIcon />
                                            </Box>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {error ? <Alert severity='error' sx={{ mb: 2 }}>{errorMsg}</Alert> : null}
            <Box sx={{ display: 'flex', pt: 2, gap: 2 }}>
                {loading ?
                    <CircularProgress
                        sx={{ ml: 6 }}>
                    </CircularProgress> :
                    <Button variant='contained' onClick={handleAddEntry}>Add Entry</Button>
                }
                {!loading && <Button
                    variant='contained'
                    sx={{ height: 'auto' }}
                    onClick={handleSubmitData}>
                    Submit Data
                </Button>}
            </Box>
        </Grid>
    )
}
